import styled from '@emotion/styled'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { colors } from '../../__styling/settings/colors'
import { themedValue } from '../../__styling/themed-value'
import { keyframes } from '@emotion/css'
import { HeartIcon } from './assets/HeartIcon'
import { transition } from '../../__styling/style-mixins'
import { breakpoint, breakpointMax } from '../../__styling/style-functions'
import { zIndex } from '../../__styling/settings/z-index'
import { WebLink } from '@news-mono/web-common'

export const telethonRedColor = '#DE1529'
export const telethonBlue = '#2A8DBF'
export const telethonBlueDark = '#0B4499'

export const heartIconClassname = 'heart-icon'

export const pulsingHeart = keyframes({
    '0%': {
        transform: 'scale( 1 )',
    },
    '20%': {
        transform: 'scale( .75 )',
    },
    '40%': {
        transform: 'scale( 1 )',
    },
    '60%': {
        transform: 'scale( .75 )',
    },
    '80%': {
        transform: 'scale( 1 )',
    },
    '100%': {
        transform: 'scale( 1 )',
    },
})

export const StyledHeart = styled(HeartIcon)({
    animation: `${pulsingHeart} 1.5s infinite`,
})

export const StyledTelethonCountdown = styled('div')(({ theme }) => ({
    backgroundColor: colors.white,
    position: 'relative',
    display: 'flex',
    fontFamily: theme.fonts.sansSerif,
    fontSize: calcRem(20),
    lineHeight: themedValue(theme, { fallback: 1.05, thewest: 1.154 }),
    overflow: 'hidden',
    boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
    maxWidth: calcRem(1220),
    borderTopRightRadius: 0,
    borderBottomRightRadius: calcRem(50),
    borderBottomLeftRadius: calcRem(50),
    zIndex: 1, // zIndex fixes issue with overflow not being hidden over a border radius
    paddingBottom: calcRem(60),

    [breakpoint('sm')]: {
        paddingBottom: 0,
    },

    [breakpoint('md')]: {
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 0,
        height: theme.kind === 'perthnow' ? calcRem(180) : calcRem(150),
    },
}))

export const StyledTelethonTrianglesLeft = styled('img')(({ theme }) => ({
    transform: 'translate(0, -120px)',
    width: 326,
    height: 208,
    position: 'absolute',

    [breakpoint('sm')]: {
        position: 'relative',
    },

    [breakpoint('md')]: {
        transform:
            theme.kind === 'perthnow'
                ? 'translate(-100px, -20px)'
                : 'translate(-20px, -20px)',
    },
}))

interface StyledTelethonShapesRightProps {
    hiddenCountdown: boolean
}

export const StyledTelethonShapesRight = styled('img')<
    StyledTelethonShapesRightProps
>(({ theme, hiddenCountdown }) => ({
    display: 'none',

    [breakpoint('md')]: {
        display:
            theme.kind === 'perthnow' || hiddenCountdown === false
                ? 'none'
                : 'block',
        position: 'absolute',
        right: 0,
        height: '150px',
        width: 'auto',
    },
}))

export const StyledTelethonKids = styled('div')(({ theme }) => ({
    display: 'none',

    [breakpoint('sm')]: {
        display: 'block',
        position: 'absolute',
        overflow: 'hidden',
        zIndex: zIndex.common.telethonKids,
        left: theme.kind === 'perthnow' ? calcRem(-80) : calcRem(-60),

        img: {
            width: 335,
        },
    },
}))

export const Content = styled('div')(({ theme }) => ({
    display: 'flex',
    transform: 'translate(0, 36px)',
    margin: '0 auto',
    textAlign: 'center',
    alignSelf: 'center',
    padding: calcRem(0, 16),
    flexDirection: theme.kind === 'perthnow' ? 'column' : 'row',

    [breakpoint('sm')]: {
        transform:
            theme.kind === 'perthnow'
                ? 'translate(-130px, 0)'
                : 'translate(-60px, 0)',
        margin: '0',
        textAlign: 'left',
        padding: 0,
    },

    [breakpointMax('md')]: {
        flexDirection: 'column',
    },
}))

export const LogoAndInfoContent = styled('div')<{ hiddenCountdown: boolean }>(
    ({ theme }) => ({
        padding: calcRem(15, 0, 0),

        p: {
            margin: themedValue(theme, {
                fallback: calcRem(10, 0),
                thewest: calcRem(15, 0),
            }),

            [`@media (max-width: 659px)`]: {
                margin: calcRem(15, 0),
            },
        },
    }),
    ({ theme }) =>
        theme.kind !== 'thewest' && {
            [`@media (min-width: 1019px)`]: {
                width: calcRem(370),

                p: {
                    fontSize: calcRem(19),
                },
            },
        },
)

export const StyledTelethonLogo = styled('img')({
    width: calcRem(240),
    height: calcRem(32),
    marginLeft: calcRem(-23),
})

export const DonateContent = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginRight: 'auto',
    padding: theme.kind !== 'thewest' ? 0 : calcRem(20),

    [`@media (max-width: 1019px)`]: {
        padding: 0,
    },

    [`@media (max-width: 659px)`]: {
        flexDirection: 'column',
        marginRight: 0,

        a: {
            marginBottom: calcRem(15),
        },
    },
}))

export const DonateNow = styled(WebLink)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    padding: calcRem(10, 20),
    backgroundColor: telethonBlue,
    color: colors.white,
    width: calcRem(170),
    height: themedValue(theme, { fallback: calcRem(40), thewest: calcRem(47) }),
    borderRadius: themedValue(theme, {
        fallback: calcRem(20),
        thewest: calcRem(24),
    }),
    textDecoration: 'none',
    outline: 'none',
    zIndex: 2, // needs to be higher than the other element thats zIndex 1 to fix an ios issue
    fontSize: themedValue(theme, {
        fallback: calcRem(20),
        sevennews: calcRem(16),
    }),
    ...transition({ properties: ['background-color'], speed: 'fast' }),

    [`@media (min-width: 660px)`]: {
        marginRight: themedValue(theme, {
            perthnow: calcRem(5),
            thewest: calcRem(5),
            sevennews: calcRem(15),
        }),
    },

    [`@media (min-width: 769px)`]: {
        marginRight: themedValue(theme, {
            perthnow: calcRem(5),
            thewest: calcRem(30),
            sevennews: calcRem(20),
        }),
    },

    svg: {
        marginRight: calcRem(10),
        path: {
            fill: colors.white,
        },
    },

    '&:hover': {
        backgroundColor: telethonBlueDark,
    },

    '&:focus': {
        color: telethonBlueDark,
        backgroundColor: colors.white,
        border: `solid 1px ${telethonBlueDark}`,
        svg: {
            path: {
                fill: telethonBlueDark,
            },
        },
    },
}))
